/**
 * DAQUMA Platform Mantine theme — the single source of truth for colour.
 *
 * **Where the palette comes from.** The brand scale below was derived from the
 * *previous* product logo: a two-tone blue, bright azure `#0163e5` and deep navy
 * `#032c64`, both at hue ~214°, reproduced at `brand[6]` and `brand[9]`.
 *
 * **That logo has been replaced and this scale has not caught up.** The current
 * mark (`public/logo-*.svg`) is navy `#051F45` with slate `#56798F` and pink
 * `#F8BBD0` accents — no azure at all. The chrome the mark sits on is pinned to
 * the mark's own navy (`BRAND_NAVY` in `NavbarPanel.tsx`) so the two never clash
 * directly, but buttons and links are still the old azure. Re-deriving the scale
 * is a design decision with a contrast budget to re-check (the table below), not
 * a find-and-replace, so it is deliberately left until someone makes that call.
 *
 * **Two custom scales, everything else built-in.** Only `brand` and `slate` are
 * hand-rolled, because only they need to match an external anchor. Accent and
 * status colours use Mantine's own palettes, which are already tuned for
 * light/dark contrast — hand-rolling six more tuples would trade that guarantee
 * for nothing. Semantic assignments (which colour means "failed", which means
 * "trigger") live in `shared/ui/palette.ts`; this file owns the *scales*, that
 * file owns the *meanings*.
 *
 * **Contrast.** Every pairing the UI actually renders was checked against WCAG
 * AA — 4.5:1 for text, 3:1 for interactive boundaries — in both schemes:
 *
 * | Pairing | Ratio | Needs |
 * |---|---|---|
 * | dark: body text `slate[0]` on body `slate[7]` | 11.15 | 4.5 |
 * | dark: dimmed `slate[2]` on body | 5.52 | 4.5 |
 * | dark: dimmed `slate[2]` on card `slate[6]` | 4.93 | 4.5 |
 * | dark: link/accent `brand[4]` on body | 4.82 | 4.5 |
 * | dark: button fill `brand[5]` vs body | 3.77 | 3.0 |
 * | dark: label on `brand[5]` fill (auto-contrast picks black) | 4.90 | 4.5 |
 * | light: brand text `brand[7]` on white | 6.85 | 4.5 |
 * | light: white label on `brand[7]` fill | 6.85 | 4.5 |
 * | light: dimmed `#4a4a4a` (brand.css) on main `#f3f3f3` | 7.99 | 4.5 |
 * | light: dimmed `#4a4a4a` (brand.css) on surface `#e5e5e5` | 7.04 | 4.5 |
 *
 * `slate[4]` (1.91 on body) is deliberately below 3:1 — it is a *decorative*
 * divider, which WCAG 1.4.11 exempts. Input borders are interactive boundaries
 * and are pinned to `slate[3]` (3.45) in the component defaults below, so the
 * one pairing that needs the ratio gets it without making every divider heavy.
 */

import { createTheme } from "@mantine/core";
import type { MantineColorsTuple, MantineThemeOverride } from "@mantine/core";

/**
 * Brand azure, hue 214.3° — the logo's own hue. Mantine convention: index 6 is
 * the base colour, 0 lightest, 9 darkest.
 *
 * `brand[6]` == the logo azure; `brand[9]` == the logo navy.
 */
const brand: MantineColorsTuple = [
  "#e9f2fe", // 0 — tint backgrounds (light-mode `variant="light"` fill)
  "#cce1fd", // 1
  "#9dc5fb", // 2
  "#67a7fb", // 3 — dark-mode dimmed accent
  "#368bfc", // 4 — dark-mode link / accent text (4.82 on body)
  "#0b73fe", // 5 — dark-mode fill (primaryShade.dark)
  "#0164e7", // 6 — BASE. The logo azure.
  "#0054c4", // 7 — light-mode fill + accent text (primaryShade.light)
  "#02439a", // 8
  "#032d66", // 9 — the logo navy.
];

/**
 * Slate: the dark-mode neutral scale, at the *same hue* as the brand but nearly
 * desaturated. It is a neutral that is related to the brand rather than a tint
 * of it, which is what keeps the dark UI reading as one system instead of grey
 * chrome with blue buttons bolted on.
 *
 * Indices follow Mantine's dark-scale convention: 0–2 text, 3 placeholder +
 * input borders, 4 dividers, 5 inputs/hover, 6 elevated surface (Paper, cards,
 * menus), 7 body, 8–9 deepest. 6-vs-7 is what gives cards their elevation, so
 * those two must stay distinct — they are the reason this scale exists at all
 * rather than using Mantine's near-black default.
 */
const slate: MantineColorsTuple = [
  "#d1d7de", // 0 — primary text
  "#b9c0cb", // 1
  "#8c98a8", // 2 — dimmed text (AA on both body and card)
  "#677589", // 3 — placeholder, input borders
  "#434e5d", // 4 — dividers (decorative)
  "#303843", // 5 — inputs, hover
  "#242a33", // 6 — elevated surface (Paper, cards, menus)
  "#1c2129", // 7 — body background
  "#14191f", // 8
  "#0e1116", // 9
];

export const theme: MantineThemeOverride = createTheme({
  primaryColor: "brand",
  // A brighter fill in dark mode so buttons carry against slate; a deeper one
  // in light mode so brand text on white clears AA.
  primaryShade: { light: 7, dark: 5 },
  // Let Mantine choose black-or-white for labels on a filled element. On the
  // bright dark-mode fill it picks black (4.90) where white would fail (4.46).
  autoContrast: true,
  defaultRadius: "md",
  colors: { brand, dark: slate },
  // The font FAMILY reads through a CSS custom property with the previous
  // literal as a fallback, so a user's saved font applies app-wide without the
  // theme object being rebuilt — and an unauthenticated or failed-fetch state
  // renders exactly what it rendered before.
  //
  // Font SIZE is deliberately NOT set here. Overriding a single Mantine size
  // token (`fontSizes.md`) changed almost no text — nearly every component in
  // this app renders at `sm` or `xs` — while still nudging the line-heights
  // derived from it, so the only visible effect was text drifting further
  // apart. Size is applied instead by rewriting every `--mantine-font-size-*`
  // and `--mantine-h*-font-size` variable at the document root; see
  // `features/settings/UserSettingsProvider.tsx`.
  fontFamily:
    "var(--daquma-font-family, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
  headings: {
    fontFamily:
      "var(--daquma-font-family, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
    fontWeight: "600",
  },
  components: {
    Button: {
      defaultProps: { radius: "xl" },
    },
    Paper: {
      defaultProps: { withBorder: false, shadow: "sm" },
    },
    // A menu fits its longest item at a readable width, never narrower.
    Menu: {
      defaultProps: { width: 220, shadow: "md" },
    },
    // Mantine cuts a NavLink label to one line ("eTMF complete…"). Sidebar
    // labels name destinations, so they wrap instead of being cut.
    NavLink: {
      styles: {
        body: { overflow: "visible" },
        label: { whiteSpace: "normal", overflow: "visible", textOverflow: "clip" },
      },
    },
    // Mantine ellipsises a squeezed badge ("Succ…", "Conc…"). Badges carry
    // statuses and verdicts, so they keep their full width instead.
    Badge: {
      styles: {
        root: { flexShrink: 0, overflow: "visible" },
        label: { overflow: "visible", textOverflow: "clip" },
      },
    },
    // An input's edge is an interactive boundary, so it owes 3:1 — the
    // decorative divider shade (slate[4], 1.91) does not clear that. Pinning
    // these three to slate[3] fixes the pairing that needs it without
    // thickening every divider in the app.
    TextInput: { styles: { input: { borderColor: "var(--mantine-color-dark-3)" } } },
    Textarea: { styles: { input: { borderColor: "var(--mantine-color-dark-3)" } } },
    Select: { styles: { input: { borderColor: "var(--mantine-color-dark-3)" } } },
  },
});

/**
 * Brand gradient for the login viewport — the logo's own navy-to-azure sweep,
 * read directly by `Login.tsx`. Darkened at both ends relative to the raw logo
 * colours so white body text over it stays comfortably above AA.
 */
export const BRAND_GRADIENT = "linear-gradient(160deg, #032d66 0%, #01337a 45%, #0143a1 100%)";
